import React, { CSSProperties, FC, useEffect, useState } from 'react'
import { Input } from '@progress/kendo-react-inputs'
import { Button } from '@progress/kendo-react-buttons'
import {
  Grid,
  GridColumn,
  GridToolbar,
  GridCellProps
} from '@progress/kendo-react-grid'
import { Label } from '@progress/kendo-react-labels'
import { Popup } from '@progress/kendo-react-popup'
import { Ripple } from '@progress/kendo-react-ripple'

import { ReferenceCommandCell } from './ReferenceCommandCell'
import './BuildingSelect.css'
import { ErrorMessage, ErrorMessages } from './ErrorMessages'
import { BuildingInfoGet } from '@gateway.Building'
import { SearchBuildingGateway } from '../../gateway/building/SearchBuildingGateway'
import { BranchSelect } from './BranchSelect'
import { useAppSelector } from '../../stores'

export interface BuildingSelectProps {
  isBtnDisabled?: boolean
  buttonOnly?: boolean
  buttonStyle?: CSSProperties
  handleSelectVal?: (val: BuildingInfoGet) => void
}

export const BuildingSelect: FC<BuildingSelectProps> = ({
  isBtnDisabled,
  buttonOnly = false,
  buttonStyle,
  handleSelectVal
}) => {
  const intePageState = {
    skip: 0,
    take: 10
  }

  const refAnchor = React.useRef<HTMLDivElement>(null)
  const [showRefSearch, setShowRefSearch] = React.useState<boolean>(false)
  const [page, setPage] = React.useState(intePageState)

  const [buildingItems, setBuildingItems] = React.useState<BuildingInfoGet[]>([])
  const [constructionNm, setConstructionNm] = React.useState<string>('')
  const [branchCd, setBranchCd] = React.useState<string>('')

  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const userProp = useAppSelector((state) => state.common.userProp)

  // 自支店を初期値としてセット
  useEffect(() => {
    setBranchCd(userProp?.branchCode ?? '')
  }, [])

  const CommandCell: FC<GridCellProps> = ({ dataItem }) => {
    const handleSelectVal = () => {
      handleCountSelectItem(dataItem)
      setShowRefSearch(false)
    }
    const data = {
      code: dataItem.procurementNo,
      name: dataItem.constructionNm
    }
    return <ReferenceCommandCell data={data} onClick={handleSelectVal} />
  }
  /**
   * ページをめくって押さえる
   */
  const pageChange = (event: {
    page: React.SetStateAction<{ skip: number, take: number }>
  }) => {
    setPage(event.page)
  }
  /**
   * 物件検索画面を開けたかどうか
   */
  const toggleRefSearch = () => {
    setShowRefSearch(!showRefSearch)
    if (!showRefSearch) {
      getSearchApi()
    }
  }
  /**
   * 物件検索画面を閉じる
   */
  const closeRefSearch = () => {
    setShowRefSearch(false)
    setConstructionNm('')
    setBranchCd(userProp?.branchCode ?? '')
  }
  /**
   * 支店検索値を取得する
   */
  const handleBranchCd = (value: string) => {
    setBranchCd(value)
  }
  /**
   * 物件名検索値を取得する
   */
  const handleInputBlur = (e: {
    target: { value: React.SetStateAction<string> }
  }) => {
    setConstructionNm(e.target.value)
  }
  /**
   * 検索ボタン押下
   */
  const handleClickSearchBtn = () => {
    setPage({ skip: 0, take: 10 })
    getSearchApi()
  }
  /**
   * リンクボタンを押した値を取得
   */
  const handleCountSelectItem = (buildingItem: GridCellProps['dataItem']) => {
    handleSelectVal?.(buildingItem)
  }
  /**
   * 物件情報一覧の取得
   */
  const getSearchApi = () => {
    SearchBuildingGateway.getSearchBuildingList(
      branchCd,
      constructionNm
    )
      .then((res) => {
        if (!res.error) {
          const buildingList = res.data.buildingInfoList.map((building) => {
            return {
              ...building.buildingInfo,
              procurementNo: `${building.buildingInfo.procurementNo ?? ''}-${building.buildingInfo.subNo ?? ''}`
            }
          })
          setBuildingItems(buildingList)
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})
  }

  return (
    <div style={{ width: '100%' }}>
      <div
        ref={refAnchor}
        style={{
          display: 'grid',
          gridTemplateColumns: buttonOnly ? 'auto' : '1fr auto'
        }}
      >
        <Button
          icon="search"
          style={buttonStyle}
          togglable
          selected={showRefSearch}
          onClick={toggleRefSearch}
          disabled={isBtnDisabled}
        />
      </div>
      <Popup
        className={'pcy-building-select-popup'}
        show={showRefSearch}
        anchor={refAnchor.current}
      >
        <Ripple>
          <Grid
            data={buildingItems?.slice(page.skip, page.take + page.skip)}
            skip={page.skip}
            take={page.take}
            total={buildingItems?.length}
            editField="inEdit"
            style={{ width: 800, height: 350 }}
            pageable
            onPageChange={pageChange}
          >
            <GridToolbar>
              <Label>支店</Label>
              <BranchSelect
                popupSettings={{ className: 'building-ddl-popup' }}
                style={{ width: '12%' }}
                value={[branchCd]}
                onChange={(valueList) => { handleBranchCd(valueList[0]) }}
                clear={true}
              />
              <Label>工事名</Label>
              <Input style={{ width: '60%' }} onBlur={handleInputBlur} />
              <Button
                className="pcy-button-short"
                themeColor="primary"
                onClick={handleClickSearchBtn}
              >
                検索
              </Button>
              <Button
                icon="close"
                themeColor="error"
                onClick={closeRefSearch}
              />
            </GridToolbar>
            <GridColumn title="支店名" field="branchNm" width={'75px'}/>
            <GridColumn title="調達番号" field="procurementNo" width={'175px'}/>
            <GridColumn title='工事名' cell={CommandCell} field="constructionNm" />
          </Grid>
        </Ripple>
      </Popup>
      <ErrorMessages style={{ zIndex: '30001' }} type="dialog" messages={errorMessages} />
    </div>
  )
}
